/**
 * clientAuth.js
 *
 * A React hook for Firebase Authentication.
 * Handles sign-in, sign-out, and tracks the authenticated user state.
 */

import { useState, useEffect, useContext, createContext } from "react";
import {
    onAuthStateChanged,
    signInWithEmailAndPassword,
    signOut,
    createUserWithEmailAndPassword,
    GoogleAuthProvider,
    signInWithPopup
} from "firebase/auth";
import { auth } from "../config/firebase";
import { checkIfUserExists, saveNewUser } from "../components/SignUp/helpers/newUser";
import toast from "react-hot-toast";
import { getUserDataFromDB } from "./fetchUserDocFromDB";

/**
 * Basic profile straight from Firebase Auth, in the same shape as a Users document.
 * Used when the Users document can't be read (not saved yet, or a network error),
 * so a signed-in user is never treated as signed out.
 * @param {Object} firebaseUser -   Firebase Auth user
 * @returns {Object}            -   { uid, userID, displayName, email, phoneNumber, pfp }
 */
const profileFromAuth = (firebaseUser) => ({
    uid         :   firebaseUser.uid,
    userID      :   firebaseUser.uid,
    displayName :   firebaseUser.displayName || "",
    email       :   firebaseUser.email || "",
    phoneNumber :   firebaseUser.phoneNumber || "",
    pfp         :   firebaseUser.photoURL || "",
});

/**
 * The user's profile: their Users document, or their Firebase Auth profile if that isn't available
 * @param {Object} firebaseUser -   Firebase Auth user
 * @returns {Object}            -   Profile
 */
const loadProfile = async (firebaseUser) => {
    const userDetails = await getUserDataFromDB(firebaseUser.uid);
    return userDetails ? { ...profileFromAuth(firebaseUser), ...userDetails } : profileFromAuth(firebaseUser);
};

// Create context to share auth state globally
const AuthContext = createContext();

/**
 * Provides auth state to children components.
 */
export const ClientAuthProvider = ({ children }) => {
    const authValue = useProvideAuth();
    return (
        <AuthContext.Provider value={authValue}>
            {children}
        </AuthContext.Provider>
    );
};

/**
 * Custom hook for accessing auth context.
 */
export const useClientAuth = () => {
    return useContext(AuthContext);
};

/**
 * Core hook that manages Firebase authentication state.
 */
function useProvideAuth() {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    // Listen for authentication state changes
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            // firebaseUser is null when signed out
            if (!firebaseUser) {
                setUser(null);
                setLoading(false);
                return;
            }
            setUser(await loadProfile(firebaseUser));
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    /**
     * Sign in with email and password
     */
    const signIn = async (email, password) => {
        try {
            const result = await signInWithEmailAndPassword(auth, email, password);
            return result.user;
        } catch (err) {
            console.error("[Auth] Sign-in failed:", err);
            throw err;
        }
    };

    /**
     * Sign up (register new user), and save their profile
     * @param {Object} profile  -   Extra profile fields: { displayName, phoneNumber }
     */
    const signUp = async (email, password, profile = {}) => {
        try {
            const result = await createUserWithEmailAndPassword(auth, email, password);
            await saveNewUser({ ...profileFromAuth(result.user), ...profile, photoURL: result.user.photoURL });
            // The auth listener may have run before the profile was saved
            setUser(await loadProfile(result.user));
            return result.user;
        } catch (err) {
            console.error("[Auth] Sign-up failed:", err);
            throw err;
        }
    };

    /**
     * Sign in with Google (popup)
     */
    const signInWithGoogle = async () => {
        try {
            const provider = new GoogleAuthProvider();
            const result = await signInWithPopup(auth, provider);
            // Update user in the db, if it doesn't exist
            const res = await checkIfUserExists(result.user);
            if (!res) {
                await saveNewUser(result.user);
                // The auth listener may have run before the profile was saved
                setUser(await loadProfile(result.user));
            }

            toast.success('User signed in successfully.');
            return result.user;
        } catch (err) {
            console.error("[Auth] Google Sign-in failed:", err);
            throw err;
        }
    };

    /**
     * Sign out user
     */
    const signOutUser = async () => {
        try {
            await signOut(auth);
            setUser(null);
        } catch (err) {
            console.error("[Auth] Sign-out failed:", err);
            throw err;
        }
    };

    return {
        user,
        loading,
        signIn,
        signUp,
        signInWithGoogle,
        signOutUser
    };
}